import { Observable } from '@apollo/client';
import { onError } from '@apollo/client/link/error';
import { SessionService } from '@domain/auth/blocs/services/session-service';
import type { GraphQLErrors, NetworkError } from '@apollo/client/errors';

export const JWT_EXPIRED_STRING = 'JWTExpired';

/**
 * When a batched request fails auth, Hasura sends back one top-level error
 * instead of per-operation results. BatchHttpLink turns that into a
 * length-mismatch ServerError and the actual error ends up buried in
 * `networkError.result`.
 *
 * @see https://github.com/hasura/graphql-engine/issues/5227
 * @param networkError - The network error to inspect.
 * @returns The error messages found in `networkError.result`, if any.
 */
const getNetworkErrorResultMessages = (
  networkError: NetworkError | undefined,
): string[] => {
  const result =
    networkError && typeof networkError === 'object' && 'result' in networkError
      ? (networkError.result as unknown)
      : undefined;
  const results = Array.isArray(result) ? result : result ? [result] : [];
  return results.flatMap((entry) => {
    const errors = (entry as { errors?: { message?: string }[] } | null)
      ?.errors;
    return Array.isArray(errors)
      ? errors.map((error) => error?.message ?? '')
      : [];
  });
};

/**
 * Whether any GraphQL error, network error, or batched-result error message
 * includes `needle`.
 *
 * @param needle - The substring to search for.
 * @param graphQLErrors - GraphQL errors returned alongside the operation.
 * @param networkError - The network error, if any.
 */
const errorsIncludeMessage = (
  needle: string,
  graphQLErrors: GraphQLErrors | undefined,
  networkError: NetworkError | undefined,
): boolean => {
  return Boolean(
    graphQLErrors?.some((error) => error.message?.includes(needle)) ||
      networkError?.message?.includes(needle) ||
      getNetworkErrorResultMessages(networkError).some((message) =>
        message.includes(needle),
      ),
  );
};

/**
 * The server rejected the JWT as expired but Amplify may still think it's
 * fine (perhaps due to clock skew), so refresh for real and retry once.
 * If the refresh fails, the entire operation fails.
 */
export const apolloTokenRefreshLink = onError(
  ({ graphQLErrors, networkError, operation, forward }) => {
    if (
      !errorsIncludeMessage(JWT_EXPIRED_STRING, graphQLErrors, networkError)
    ) {
      return;
    }

    // If the refresh was already attempted, give up instead of looping
    if (operation.getContext().jwtRefreshAttempted) {
      return;
    }

    return new Observable((observer) => {
      // Don't re-send the operation if the caller unsubscribed while we
      // were waiting on the refresh
      let cancelled = false;
      let retrySubscription: { unsubscribe: () => void } | undefined;

      SessionService.refreshIdTokenOnce()
        .then(() => {
          if (cancelled) {
            return;
          }

          operation.setContext({ jwtRefreshAttempted: true });
          retrySubscription = forward(operation).subscribe(observer);
        })
        .catch((refreshError: unknown) => {
          observer.error(refreshError);
        });

      return () => {
        cancelled = true;
        retrySubscription?.unsubscribe();
      };
    });
  },
);
